import { Suspense, useContext } from "react";
import { TableSkeleton } from "../../components/Skeleton/TableSkeleton";
import { DataTable } from "../../components/ui/data-table";
import { formatToShortDate, formatUniversalNumber } from "../../lib/utils";
import { InvoiceContext } from "../../providers/InvoiceProvider";

const InvoiceTable = () => {
  const columns = [
    {
      accessorKey: "invoiceId",
      header: "",
      cell: ({ row }: any) => {
        const status =
          row.original.invoiceStatusText === "Paid"
            ? "paid"
            : row.original.invoiceStatusText === "Partially Paid"
            ? "partially paid"
            : row.original.invoiceStatusText === "Active"
            ? "active"
            : row.original.invoiceStatusText === "Cancelled"
            ? "cancelled"
            : "";

        const badgeStyles: { [key: string]: string } = {
          paid: "bg-[#0F8123]",
          "partially paid": "bg-[#cf723dc9]",
          active: "bg-[#0C53B7]",
          cancelled: "bg-[#B42318]",
        };

        return (
          <p className="flex items-center justify-center space-x-2 p-2">
            <span className={`h-2 w-2 rounded-full ${badgeStyles[status]}`} />
          </p>
        );
      },
      enableSorting: false,
      size: 20,
    },
    {
      accessorKey: "category_id",
      header: "Invoice No",
      cell: ({ row }: any) => {
        return (
          <span className="BookEZ_font_body_medium">
            {row.original.refInvoiceId}
          </span>
        );
      }, // Set value from `refInvoiceId`
    },
    {
      accessorKey: "refInvoiceDueDate",
      header: "Date",
      cell: ({ row }: any) => formatToShortDate(row.original.refInvoiceDate),
    },
    {
      accessorKey: "date",
      header: "Due Date",
      cell: ({ row }: any) => formatToShortDate(row.original.refInvoiceDueDate),
    },
    {
      accessorKey: "name",
      header: "Client Name",
      cell: ({ row }: any) => row.original.customerName, // Set value from `customerName`
    },
    {
      accessorKey: "amount",
      header: () => (
        <span style={{ width: "100px" }} className="w-full text-right block">
          Total Amount
        </span>
      ),
      cell: ({ row }: any) => {
        const amount: number = row.original.totalAmt;
        return (
          <span style={{ width: "100px" }} className="text-right block">
            {formatUniversalNumber(amount)}
          </span>
        );
      },
    },
    {
      accessorKey: "invoiceStatusText",
      header: "Status",
      cell: ({ row }: any) => {
        const status =
          row.original.invoiceStatusText === "Paid"
            ? "paid"
            : row.original.invoiceStatusText === "Partially Paid"
            ? "partially paid"
            : row.original.invoiceStatusText === "Active"
            ? "active"
            : row.original.invoiceStatusText === "Cancelled"
            ? "cancelled"
            : "";

        const badgeStyles: { [key: string]: string } = {
          paid: "bg-[#ECFDF3] text-[#027A48]",
          "partially paid": "bg-[#FFF4E5] text-[#B54708]",
          active: "bg-[#a8c0e080] text-[#0C53B7]",
          cancelled: "bg-[#FEF2F1] text-[#B42318]",
        };

        return (
          <span
            className={`BookEZ_font_body_size BookEZ_font_body_medium px-3 py-1 rounded-[4px] ${badgeStyles[status]}`}
          >
            {status}
          </span>
        );
      },
    },
  ];

  const {
    invoices,
    isLoading,
    pageNumber,
    pageSize,
    totalPages,
    totalRecords,
    handlePageChange,
    handlePageSizeChange,
  } = useContext(InvoiceContext);

  return (
    <div>
      {isLoading ? (
        <TableSkeleton />
      ) : (
        <Suspense fallback={<TableSkeleton />}>
          <DataTable
            columns={columns}
            data={invoices}
            showPagination={true}
            pageSize={pageSize}
            currentPage={pageNumber}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            totalRecords={totalRecords}
          />
        </Suspense>
      )}
    </div>
  );
};

export default InvoiceTable;
